import { fromPairs } from 'lodash';

import { ComponentProps } from 'react';

import { Button, ButtonLink, Card, ListLayout } from '@ingrid/luterkort-react';

import { ClearConfirmationModal } from '~src/components/ClearConfirmationModal/ClearConfirmationModal';
import { KeyValueFormDialog } from '~src/components/KeyValueFormDialog/KeyValueFormDialog';
import { useTranslation } from '~src/i18n/use-translation';

import { useShipmentMetadataFieldQuery } from '../queries';
import { useShipmentFormContext } from '../types';

export function Metadata() {
  const { watch, setValue } = useShipmentFormContext();
  const { t } = useTranslation();

  const shippingMethodId = watch('shipping_method.id');
  const value = watch('meta');

  const { data: carrierMetaFields = [] } = useShipmentMetadataFieldQuery({
    shippingMethodId,
  });

  const meta = [
    /**
     * Append any carrier meta fields to the very start
     * But, if the key is already added, we ensure to exclude it.
     */
    ...carrierMetaFields
      .filter(cm => !Object.keys(value ?? {}).includes(cm.key))
      .map(cm => ({ key: cm.key, value: '' })),
    /**
     * We also include the actual value, acting as a `defaultValue`.
     */
    ...Object.entries(value ?? {}).map(([key, value]) => ({ key, value })),
  ];

  const formDialogProps: Omit<ComponentProps<typeof KeyValueFormDialog>, 'trigger'> = {
    onValueChange: data => setValue('meta', fromPairs(data.map(entry => [entry.key, entry.value]))),
    // force fallback / default value
    value: meta.length > 0 ? meta : undefined,
    title: t('common.metadata'),
  };

  return (
    <Card.List>
      <Card.Wrapper as="section">
        <Card.Header>{t('common.metadata')}</Card.Header>

        <Card.Content>
          {meta.length > 0 ? (
            <ListLayout.Column data-testid="metadata">
              <ListLayout.Row>
                <ListLayout.Column>
                  {meta.map(({ key, value }) => (
                    <ListLayout.Row key={key}>
                      <ListLayout.Stack>
                        <ListLayout.SecondaryText>{key}</ListLayout.SecondaryText>
                        <ListLayout.PrimaryText>{value.length > 0 ? value : '-'}</ListLayout.PrimaryText>
                      </ListLayout.Stack>
                    </ListLayout.Row>
                  ))}
                </ListLayout.Column>

                <ListLayout.Actions>
                  <KeyValueFormDialog
                    trigger={<ButtonLink>{t('common.edit')}</ButtonLink>}
                    // Force re-render if meta was changed
                    key={`one-${meta.map(m => m.key).join(',')}`}
                    {...formDialogProps}
                  />

                  <ClearConfirmationModal
                    clearedItemName={`common.metadata`}
                    onSubmit={() => setValue('meta', undefined)}
                  />
                </ListLayout.Actions>
              </ListLayout.Row>
            </ListLayout.Column>
          ) : (
            <KeyValueFormDialog
              trigger={<Button variant="secondary">{t('shipments.create.addMetadata')}</Button>}
              {...formDialogProps}
            />
          )}
        </Card.Content>
      </Card.Wrapper>
    </Card.List>
  );
}
